import React, { useState, useEffect, useMemo } from 'react';
import CgmModulePageLayout from './layout/CgmModulePageLayout';
import './CgmFilterToolbar.css';
import './CgmModulePage.css';
import { authJsonHeaders } from '../utils/authHeaders';
import { useAuth } from '../contexts/AuthContext';
import {
  CGM_MONTH_OPTIONS,
  getCurrentMonthYear,
  filterByMonthYear,
  buildYearOptions,
} from '../utils/cgmMonthYearFilter';

const { month: DEFAULT_FILTER_MONTH, year: DEFAULT_FILTER_YEAR } = getCurrentMonthYear();

const ACTION_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'CREATE', label: 'Création' },
  { value: 'UPDATE', label: 'Modification' },
  { value: 'DELETE', label: 'Suppression' },
  { value: 'PRINT', label: 'Impression' },
  { value: 'OTHER', label: 'Autre' },
];

const ENTITY_LABELS = {
  customer: 'Client',
  customer_operation: 'Opération client',
  invoice: 'Facture / NH',
  cash_drop: 'Caisse CGM',
  receipt_print: 'Reçu',
  etat_print: 'État CGM',
  user: 'Utilisateur',
  other: 'Autre',
};

export default function TracabilitePage() {
  const { activeCabinet } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterMonth, setFilterMonth] = useState(DEFAULT_FILTER_MONTH);
  const [filterYear, setFilterYear] = useState(DEFAULT_FILTER_YEAR);
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [detailRow, setDetailRow] = useState(null);

  const isAdminTrace = (row) => {
    const role = String(row?.user_role || '').trim().toLowerCase();
    const name = String(row?.username || '').trim().toLowerCase();
    return role === 'admin' || role === 'superadmin' || name === 'admin';
  };

  const applySearch = () => {
    setSearchQuery(searchDraft);
  };

  const handleDeleteRow = async (row) => {
    if (!row?.id) return;
    if (!window.confirm('Supprimer cette entrée de traçabilité ?')) return;
    try {
      const response = await fetch(`/api/tracabilite/${row.id}`, {
        method: 'DELETE',
        headers: authJsonHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || 'Suppression impossible');
      }
      setRows((prev) => prev.filter((r) => Number(r.id) !== Number(row.id)));
      if (detailRow?.id === row.id) setDetailRow(null);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Erreur lors de la suppression');
      window.alert(err.message || 'Erreur lors de la suppression');
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCabinet?.id]);

  const fetchRows = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/tracabilite?limit=1000', {
        cache: 'no-store',
        headers: authJsonHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setRows(data.rows || []);
        setFilterUser('');
      } else {
        setError(data.error || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error(err);
      setError('Erreur réseau lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getRowDate = (row) => row.created_at;

  const visibleRows = useMemo(() => rows || [], [rows]);

  const yearOptions = useMemo(
    () => buildYearOptions(visibleRows, getRowDate),
    [visibleRows]
  );

  const userOptions = useMemo(() => {
    const map = new Map();
    visibleRows.forEach((r) => {
      const name = String(r.username || '').trim();
      if (!name) return;
      if (!map.has(name)) {
        map.set(name, { username: name, role: r.user_role || '' });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.username.localeCompare(b.username, 'fr', { sensitivity: 'base' })
    );
  }, [visibleRows]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const actionLabel = (action) => {
    const found = ACTION_OPTIONS.find((o) => o.value === String(action || '').toUpperCase());
    return found?.label || action || '—';
  };

  const actionClass = (action) => {
    const a = String(action || '').toUpperCase();
    if (a === 'CREATE') return 'create';
    if (a === 'UPDATE') return 'update';
    if (a === 'DELETE') return 'delete';
    if (a === 'PRINT') return 'print';
    return 'other';
  };

  const entityLabel = (type) => ENTITY_LABELS[type] || type || '—';

  const customerLabel = (row) => {
    if (row.customer_name) return row.customer_name;
    if (row.customer_id) return `#${row.customer_id}`;
    return '—';
  };

  const filteredRows = useMemo(() => {
    const q = String(searchQuery || '').trim().toLowerCase();
    let list = q
      ? [...visibleRows]
      : filterByMonthYear(visibleRows, filterMonth, filterYear, getRowDate);

    if (filterAction) {
      const act = filterAction.toUpperCase();
      list = list.filter((r) => String(r.action || '').toUpperCase() === act);
    }

    if (filterUser) {
      const u = String(filterUser).trim().toLowerCase();
      list = list.filter((r) => String(r.username || '').trim().toLowerCase() === u);
    }

    if (!q) return list;

    return list.filter((r) => {
      const hay = [
        r.username,
        r.user_role,
        r.action,
        actionLabel(r.action),
        r.entity_type,
        entityLabel(r.entity_type),
        r.entity_id,
        r.customer_id,
        r.customer_name,
        r.summary,
        r.ip_address,
        r.request_path,
        formatDateTime(r.created_at),
      ]
        .filter((v) => v != null && v !== '')
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [visibleRows, filterMonth, filterYear, filterAction, filterUser, searchQuery]);

  const selectedMonthLabel = CGM_MONTH_OPTIONS.find((m) => m.value === filterMonth)?.label;

  if (loading && rows.length === 0) {
    return (
      <CgmModulePageLayout crumb="Comptabilité CGM · Traçabilité" backTo="/" wide className="tracabilite-page">
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: '#0f766e' }} role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </CgmModulePageLayout>
    );
  }

  return (
    <CgmModulePageLayout crumb="Comptabilité CGM · Traçabilité" backTo="/" wide className="tracabilite-page">
      <style jsx global>{`
        .tracabilite-page .form-container {
          background: #fff;
          border-radius: 14px;
          padding: 0;
          overflow: hidden;
          border: 1px solid #99f6e4;
          box-shadow: 0 8px 24px rgba(17, 94, 89, 0.08);
        }
        .tracabilite-page .modern-table-header {
          background: linear-gradient(135deg, #0f766e 0%, #115e59 100%);
          color: #fff;
          padding: 1rem 1.1rem 1.05rem;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 12px;
        }
        .tracabilite-page .modern-table-header .header-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          font-weight: 700;
          font-size: 1rem;
        }
        .tracabilite-page .header-filters-scroll {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
          width: 100%;
        }
        @media (max-width: 900px) {
          .tracabilite-page .header-filters-scroll {
            grid-template-columns: 1fr;
          }
        }
        .tracabilite-page .header-filters-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }
        .tracabilite-page .counter {
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.35);
          color: #fff;
          padding: 4px 12px;
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 700;
          white-space: nowrap;
        }
        .tracabilite-page .trace-filter-card {
          background: rgba(255, 255, 255, 0.14);
          border: 1px solid rgba(255, 255, 255, 0.32);
          border-radius: 12px;
          padding: 0.65rem 0.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.45rem;
          min-width: 0;
        }
        .tracabilite-page .trace-filter-card__title {
          font-size: 0.7rem;
          font-weight: 800;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          opacity: 0.9;
          margin: 0;
        }
        .tracabilite-page .trace-filter-card__row {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .tracabilite-page .caisse-month-year-filters label {
          color: #fff;
          font-weight: 700;
          font-size: 0.72rem;
          margin: 0;
          white-space: nowrap;
        }
        .tracabilite-page .caisse-month-year-select {
          border: 1px solid #e9e5ff;
          border-radius: 8px;
          padding: 0.4rem 0.55rem;
          font-size: 0.78rem;
          font-weight: 600;
          color: #115e59;
          background: #fff;
          min-height: 34px;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
        }
        .tracabilite-page .trace-search-input {
          width: 150px;
          max-width: 150px;
          min-width: 110px;
          flex: 0 0 auto;
        }
        .tracabilite-page .trace-btn-search {
          border: none;
          border-radius: 8px;
          padding: 0.4rem 0.8rem;
          min-height: 34px;
          font-size: 0.78rem;
          font-weight: 700;
          color: #fff;
          background: #312e81;
          cursor: pointer;
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .tracabilite-page .trace-btn-search:hover {
          background: #1e1b4b;
        }
        .tracabilite-page .trace-row-actions {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .tracabilite-page .btn-detail {
          border: 1px solid #99f6e4;
          background: #f0fdfa;
          color: #115e59;
          border-radius: 8px;
          padding: 0.25rem 0.55rem;
          font-size: 0.75rem;
          font-weight: 700;
          cursor: pointer;
        }
        .tracabilite-page .btn-detail:hover {
          background: #ccfbf1;
        }
        .tracabilite-page .btn-trace-delete {
          border: 1px solid #fecaca;
          background: #fef2f2;
          color: #dc2626;
          border-radius: 8px;
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
        }
        .tracabilite-page .btn-trace-delete:hover {
          background: #fee2e2;
        }
        .tracabilite-page .btn-trace-delete svg {
          width: 15px;
          height: 15px;
          display: block;
          stroke: #dc2626;
        }
        .tracabilite-page .modern-table-wrapper {
          overflow-x: auto;
          background: #fff;
        }
        .tracabilite-page .modern-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }
        .tracabilite-page .modern-table thead th {
          background: #f0fdfa;
          color: #4338a8;
          font-weight: 700;
          padding: 0.65rem 0.75rem;
          text-align: left;
          border-bottom: 1px solid #e4dff7;
          white-space: nowrap;
        }
        .tracabilite-page .modern-table tbody td {
          padding: 0.6rem 0.75rem;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          vertical-align: middle;
        }
        .tracabilite-page .modern-table tbody tr:hover {
          background: #faf9ff;
        }
        .tracabilite-page .modern-empty-state {
          text-align: center;
          padding: 2.5rem 1rem;
          color: #64748b;
        }
        .tracabilite-page .modern-empty-state i {
          font-size: 2rem;
          opacity: 0.5;
          margin-bottom: 0.75rem;
          display: block;
        }
        .tracabilite-page .trace-action-badge {
          display: inline-block;
          padding: 0.2rem 0.55rem;
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 700;
          white-space: nowrap;
        }
        .tracabilite-page .trace-action-badge.create { background: #dcfce7; color: #166534; }
        .tracabilite-page .trace-action-badge.update { background: #fef9c3; color: #854d0e; }
        .tracabilite-page .trace-action-badge.delete { background: #fee2e2; color: #991b1b; }
        .tracabilite-page .trace-action-badge.auth { background: #e0e7ff; color: #3730a3; }
        .tracabilite-page .trace-action-badge.print { background: #e2e8f0; color: #334155; }
        .tracabilite-page .trace-action-badge.other { background: #f1f5f9; color: #475569; }
        .tracabilite-page .trace-detail-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.45);
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }
        .tracabilite-page .trace-detail-modal {
          background: #fff;
          border-radius: 12px;
          max-width: 640px;
          width: 100%;
          max-height: 80vh;
          overflow: auto;
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
          padding: 1.25rem;
        }
        .tracabilite-page .trace-detail-modal h3 {
          margin: 0 0 0.75rem;
          color: #4338a8;
          font-size: 1.05rem;
        }
        .tracabilite-page .trace-detail-modal pre {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 0.75rem;
          font-size: 0.78rem;
          overflow: auto;
          white-space: pre-wrap;
          word-break: break-word;
        }
      `}</style>

      <div className="cgm-module-banner">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h2 className="mb-0">
              <i className="fas fa-route me-2" aria-hidden="true" />
              Traçabilité
              {activeCabinet?.name ? (
                <span className="ms-2" style={{ fontSize: '0.75em', fontWeight: 500, opacity: 0.9 }}>
                  · {activeCabinet.name}
                </span>
              ) : null}
            </h2>
            <p className="mb-0 mt-1" style={{ fontSize: '0.9rem', opacity: 0.9 }}>
              Journal du cabinet actif (utilisateurs et actions séparés)
            </p>
          </div>
          <div className="text-end">
            <div className="cgm-module-banner__label">
              {searchQuery.trim() || filterAction || filterUser
                ? 'Entrées (filtre)'
                : `Entrées ${selectedMonthLabel} ${filterYear}`}
            </div>
            <div className="cgm-module-banner__value">{filteredRows.length}</div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2" />
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} aria-label="Fermer" />
        </div>
      )}

      <div className="form-container">
        <div className="modern-table-container">
          <div className="modern-table-header">
            <div className="header-title">
              <span>
                <i className="fas fa-history header-icon me-2" aria-hidden="true" />
                Journal des actions
              </span>
              <span className="counter">
                {filteredRows.length}
                {visibleRows.length ? ` / ${visibleRows.length}` : ''}
              </span>
            </div>

            <div className="header-filters-scroll">
              <div className="trace-filter-card">
                <p className="trace-filter-card__title">Période</p>
                <div className="trace-filter-card__row">
                  <label htmlFor="trace-filter-month">Mois</label>
                  <select
                    id="trace-filter-month"
                    className="caisse-month-year-select"
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(parseInt(e.target.value, 10))}
                  >
                    {CGM_MONTH_OPTIONS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  <label htmlFor="trace-filter-year">Année</label>
                  <select
                    id="trace-filter-year"
                    className="caisse-month-year-select caisse-month-year-select--year"
                    value={filterYear}
                    onChange={(e) => setFilterYear(parseInt(e.target.value, 10))}
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="trace-filter-card">
                <p className="trace-filter-card__title">Type d&apos;événement</p>
                <div className="trace-filter-card__row">
                  <label htmlFor="trace-filter-action">Événement</label>
                  <select
                    id="trace-filter-action"
                    className="caisse-month-year-select"
                    value={filterAction}
                    onChange={(e) => setFilterAction(e.target.value)}
                  >
                    {ACTION_OPTIONS.map((o) => (
                      <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="trace-filter-card__row">
                  <label htmlFor="trace-filter-user">Utilisateur</label>
                  <select
                    id="trace-filter-user"
                    className="caisse-month-year-select"
                    value={filterUser}
                    onChange={(e) => setFilterUser(e.target.value)}
                  >
                    <option value="">Tous</option>
                    {userOptions.map((u) => (
                      <option key={u.username} value={u.username}>
                        {u.username}{u.role ? ` (${u.role})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="trace-filter-card">
                <p className="trace-filter-card__title">Recherche</p>
                <div className="trace-filter-card__row">
                  <input
                    id="trace-filter-search"
                    type="search"
                    className="caisse-month-year-select trace-search-input"
                    placeholder="Utilisateur, client…"
                    value={searchDraft}
                    onChange={(e) => setSearchDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applySearch();
                      }
                    }}
                    aria-label="Rechercher dans la traçabilité"
                  />
                  <button
                    type="button"
                    className="trace-btn-search"
                    onClick={applySearch}
                  >
                    <i className="fas fa-search" aria-hidden="true" />
                    Chercher
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modern-table-wrapper">
            {visibleRows.length === 0 ? (
              <div className="modern-empty-state">
                <i className="fas fa-inbox" aria-hidden="true" />
                <p>Aucune entrée de traçabilité pour le moment</p>
                <p style={{ fontSize: '0.85rem', marginTop: '0.35rem' }}>
                  Les actions des caissiers et de l’admin apparaissent ici.
                </p>
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="modern-empty-state">
                <i className="fas fa-calendar-times" aria-hidden="true" />
                <p>
                  {searchQuery.trim() || filterAction || filterUser
                    ? 'Aucun résultat pour cette recherche / ce filtre'
                    : `Aucune entrée pour ${selectedMonthLabel} ${filterYear}`}
                </p>
              </div>
            ) : (
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Utilisateur</th>
                    <th>Événement</th>
                    <th>Type</th>
                    <th>Client</th>
                    <th>Résumé</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.created_at)}</td>
                      <td>
                        {row.username || '—'}
                        {(row.user_role || isAdminTrace(row)) ? (
                          <div style={{
                            fontSize: '0.72rem',
                            color: isAdminTrace(row) ? '#0f766e' : '#94a3b8',
                            fontWeight: isAdminTrace(row) ? 600 : 400,
                          }}>
                            {isAdminTrace(row) ? 'Admin' : row.user_role}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <span className={`trace-action-badge ${actionClass(row.action)}`}>
                          {actionLabel(row.action)}
                        </span>
                      </td>
                      <td>{entityLabel(row.entity_type)}</td>
                      <td>{customerLabel(row)}</td>
                      <td title={row.summary || ''}>{row.summary || '—'}</td>
                      <td>
                        <div className="trace-row-actions">
                          <button
                            type="button"
                            className="btn-detail"
                            onClick={() => setDetailRow(row)}
                            title="Voir le détail"
                          >
                            Voir
                          </button>
                          <button
                            type="button"
                            className="btn-trace-delete"
                            onClick={() => handleDeleteRow(row)}
                            title="Supprimer l'entrée"
                            aria-label="Supprimer l'entrée"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              <line x1="10" y1="11" x2="10" y2="17" />
                              <line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {detailRow && (
        <div
          className="trace-detail-overlay"
          role="presentation"
          onClick={() => setDetailRow(null)}
        >
          <div
            className="trace-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Détail traçabilité"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
              <h3>Détail — #{detailRow.id}</h3>
              <button type="button" className="btn-close" onClick={() => setDetailRow(null)} aria-label="Fermer" />
            </div>
            <p style={{ margin: '0 0 0.5rem', color: '#475569', fontSize: '0.9rem' }}>
              {formatDateTime(detailRow.created_at)} · {actionLabel(detailRow.action)} · {entityLabel(detailRow.entity_type)}
            </p>
            <p style={{ margin: '0 0 1rem', fontWeight: 600 }}>{detailRow.summary}</p>
            <div style={{ marginBottom: '0.75rem' }}>
              <strong>Avant</strong>
              <pre>{detailRow.old_values ? JSON.stringify(detailRow.old_values, null, 2) : '—'}</pre>
            </div>
            <div>
              <strong>Après</strong>
              <pre>{detailRow.new_values ? JSON.stringify(detailRow.new_values, null, 2) : '—'}</pre>
            </div>
          </div>
        </div>
      )}
    </CgmModulePageLayout>
  );
}
